/**
 * Resolución del tenant "actual" para el customer SPA.
 *
 * Estrategia:
 *  - El slug se extrae del hostname: `{slug}.app.{env}.zen.zervizdev.com` →
 *    primer label. También soporta hostnames simples `{slug}.localhost`.
 *  - En local-dev sin subdominio real (vite en 5175), `config.tenantSlugOverride`
 *    permite forzar el slug manualmente.
 *  - El UUID interno del tenant se resuelve llamando a `listTenants()` y
 *    matcheando por slug. Se cachea con react-query.
 *
 * ASSUMPTION: el endpoint /v1/tenants devuelve el tenant del usuario actual
 * (filtrado por `custom:tenant_id` server-side cuando el rol no es
 * platform-ops). Si en realidad devuelve TODOS los tenants para el rol admin,
 * AuthGuard aún valida la correspondencia tenant_id ↔ slug.
 */
import { useQuery } from '@tanstack/react-query';
import { config } from '../config.js';
import { listTenants, type Tenant } from './tenants.js';

/**
 * Devuelve el slug del tenant según subdomain o el override de dev.
 * Retorna null si no se pudo determinar.
 */
export function detectTenantSlug(): string | null {
  if (config.tenantSlugOverride) return config.tenantSlugOverride;

  const host = window.location.hostname;
  if (!host) return null;

  // ipv4 / localhost → no slug detectable, dejá que dev use override
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(host)) return null;
  if (host === 'localhost' || host === '127.0.0.1') return null;

  const parts = host.split('.');
  // El primer label debe ser el slug (`acme.app.dev.zen.zervizdev.com`,
  // `acme.localhost`, etc.).
  if (parts.length < 2) return null;
  const first = parts[0];
  if (!first || first === 'app' || first === 'www') return null;
  return first;
}

export interface TenantContext {
  /** Slug detectado del hostname (o override). */
  slug: string | null;
  /** Tenant resuelto vía listTenants (id + name + ...). */
  tenant: Tenant | null;
  isLoading: boolean;
  error: unknown;
}

/**
 * Hook que combina slug detectado + tenant resuelto.
 * Se usa desde AuthGuard, Topbar y todas las páginas que necesitan tenantId.
 */
export function useTenantContext(): TenantContext {
  const slug = detectTenantSlug();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants', 'customer-context'],
    queryFn: () => listTenants({ limit: 200 }),
    staleTime: 5 * 60_000,
  });
  const tenants = data?.items ?? [];
  const tenant = slug ? tenants.find((t) => t.slug === slug) ?? null : null;
  return { slug, tenant, isLoading, error };
}
